import React, { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import AppShell from "@/components/AppShell";
import { toast } from "sonner";
import {
  RocketLaunch,
  ListChecks,
  Article,
  Calendar,
  DownloadSimple,
  Copy,
} from "@phosphor-icons/react";

/**
 * GuidaInstagram — pagina dedicata col playbook Instagram di Pronto.
 *
 * Carica al volo i 4 markdown da `/guides/*.md` (asset statici nel
 * `public/`), li renderizza con react-markdown e offre azioni rapide:
 * copia tutto + scarica .md. Pensato per founder/agente che vuole
 * partire da zero su Instagram con contenuti già pronti.
 */
const GUIDES = [
  {
    slug: "kickstart",
    file: "instagram_30day_kickstart.md",
    label: "Playbook 30 giorni",
    desc: "Da 0 follower a primi clienti in 30 giorni · giorno-per-giorno con contenuti pronti",
    icon: RocketLaunch,
    badge: "Inizia qui",
    badgeColor: "#7C8B6E",
  },
  {
    slug: "setup",
    file: "instagram_step_by_step_guide.md",
    label: "Setup profilo",
    desc: "35 minuti per aprire e configurare l'account business al meglio",
    icon: ListChecks,
  },
  {
    slug: "content",
    file: "instagram_content_pack_no_video.md",
    label: "Libreria contenuti",
    desc: "10 post + 8 story + 5 carousel · caption pronte da copiare",
    icon: Article,
  },
  {
    slug: "management",
    file: "instagram_page_management_guide.md",
    label: "Gestione settimanale",
    desc: "Routine post-setup · roadmap 0→1.000 follower · script DM",
    icon: Calendar,
  },
];

export default function GuidaInstagram() {
  const [activeSlug, setActiveSlug] = useState("kickstart");
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);

  const active = useMemo(
    () => GUIDES.find((g) => g.slug === activeSlug),
    [activeSlug],
  );

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    setLoading(true);
    fetch(`/guides/${active.file}`)
      .then((r) => r.text())
      .then((txt) => {
        if (cancelled) return;
        setContent(txt);
        // scroll back to top on guide change
        window.scrollTo({ top: 0, behavior: "smooth" });
      })
      .catch(() => {
        if (!cancelled) toast.error("Impossibile caricare la guida.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [active]);

  const onCopyAll = async () => {
    try {
      await navigator.clipboard.writeText(content);
      toast.success("Guida copiata. Incollala su Notion / Google Doc / Apple Notes.");
    } catch {
      toast.error("Copia fallita. Prova a scaricare il file.");
    }
  };

  const onDownload = () => {
    const blob = new Blob([content], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = active.file;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <AppShell title="Guida Instagram">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {/* ── Header ───────────────────────────────────────────── */}
        <div className="mb-6">
          <span className="overline text-[#7C8B6E]">Marketing · Growth</span>
          <h1 className="font-editorial text-3xl sm:text-4xl text-[#1C1C1A] mt-2 leading-tight">
            Lancia la pagina Instagram di Pronto
          </h1>
          <p className="text-[14px] text-[#6B6A65] mt-2 max-w-2xl leading-snug">
            4 documenti che ti portano da{" "}
            <strong className="text-[#1C1C1A]">zero follower</strong> a{" "}
            <strong className="text-[#1C1C1A]">primi clienti pagati</strong> in 30 giorni.
            Setup, contenuti già scritti, script DM, calendario settimanale.
          </p>
        </div>

        {/* ── Guide picker ─────────────────────────────────────── */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {GUIDES.map((g) => {
            const Icon = g.icon;
            const isActive = g.slug === activeSlug;
            return (
              <button
                key={g.slug}
                type="button"
                onClick={() => setActiveSlug(g.slug)}
                className={`relative text-left p-4 rounded-lg border-2 transition-all ${
                  isActive
                    ? "border-[#1C1C1A] bg-[#1C1C1A] text-white"
                    : "border-[#EAE7DF] bg-white hover:border-[#7C8B6E]/60"
                }`}
                data-testid={`guida-tab-${g.slug}`}
              >
                {g.badge && (
                  <span
                    className={`absolute top-2 right-2 text-[8.5px] font-bold tracking-[0.18em] uppercase px-1.5 py-0.5 rounded-sm leading-none ${
                      isActive ? "bg-white text-[#1C1C1A]" : "bg-[#7C8B6E] text-white"
                    }`}
                  >
                    {g.badge}
                  </span>
                )}
                <Icon
                  size={20}
                  weight="duotone"
                  className={isActive ? "text-white mb-2" : "text-[#7C8B6E] mb-2"}
                />
                <div className="text-[13px] font-bold leading-tight">{g.label}</div>
                <div
                  className={`text-[11px] mt-1.5 leading-snug ${
                    isActive ? "text-white/70" : "text-[#6B6A65]"
                  }`}
                >
                  {g.desc}
                </div>
              </button>
            );
          })}
        </div>

        {/* ── Content + Actions ────────────────────────────────── */}
        <div className="bg-white rounded-xl border border-[#EAE7DF] overflow-hidden">
          <div className="flex items-center justify-between px-4 sm:px-5 py-3 border-b border-[#EAE7DF] bg-[#FAFAF7]">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#7C8B6E]">
              {active?.label}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onCopyAll}
                disabled={loading || !content}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-[#EAE7DF] text-[12px] font-semibold text-[#1C1C1A] hover:bg-[#F1F4ED] disabled:opacity-50"
                data-testid="guida-copy-btn"
              >
                <Copy size={13} weight="bold" /> Copia tutto
              </button>
              <button
                type="button"
                onClick={onDownload}
                disabled={loading || !content}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#1C1C1A] text-white text-[12px] font-semibold hover:bg-black disabled:opacity-50"
                data-testid="guida-download-btn"
              >
                <DownloadSimple size={13} weight="bold" /> Scarica .md
              </button>
            </div>
          </div>

          <article
            className="prose prose-pronto px-5 sm:px-8 py-6 sm:py-8 max-w-none"
            data-testid={`guida-content-${activeSlug}`}
          >
            {loading ? (
              <div className="space-y-3">
                <div className="h-7 w-2/3 rounded bg-[#EAE7DF]/60 animate-pulse" />
                <div className="h-4 w-full rounded bg-[#EAE7DF]/40 animate-pulse" />
                <div className="h-4 w-5/6 rounded bg-[#EAE7DF]/40 animate-pulse" />
                <div className="h-4 w-4/5 rounded bg-[#EAE7DF]/40 animate-pulse" />
              </div>
            ) : (
              <ReactMarkdown>{content}</ReactMarkdown>
            )}
          </article>
        </div>

        {/* ── Footer hint ─────────────────────────────────────── */}
        <div className="mt-6 text-[12px] text-[#6B6A65] text-center leading-snug">
          💡 Ti suggerisco di scaricare il <strong>Playbook 30 giorni</strong> come file{" "}
          <code className="px-1 py-0.5 bg-[#F1F4ED] rounded text-[11px]">.md</code> e
          aprirlo su Notion / Apple Notes / Obsidian — così puoi spuntare i checkbox giorno per giorno.
        </div>
      </div>
    </AppShell>
  );
}
